import { useState } from "preact/hooks"

import { Link } from "wouter-preact"

import { LedgerAccountJson, OpenDeductionsScope } from "@/api/accounting.tsx"
import { updateIntegration } from "@/api/integration"
import { AutocompleteSelect, SelectItem } from "@/accounting/autocomplete_select.tsx"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem as SelectOption, SelectTrigger, SelectValue } from "@/components/ui/select"
import { toast } from "@/components/ui/use-toast"
import { OPEN_DEDUCTIONS_SCOPE_OPTIONS } from "@/settings/integrations/open-deductions-settings"

const NONE_ID = "__none__"

/** Options for the holding-account picker: every numbered GL account plus an explicit "off". */
export function holding_account_options(accounts: Pick<LedgerAccountJson, "account_number" | "expense_account">[]): SelectItem[] {
  const seen = new Set<string>()
  const options: SelectItem[] = [{ id: NONE_ID, name: "None — every deduction must be coded before applying" }]
  for (const account of accounts) {
    if (!account.account_number || seen.has(account.account_number)) continue
    seen.add(account.account_number)
    options.push({ id: account.account_number, name: `${account.account_number} — ${account.expense_account}` })
  }
  return options
}

/** One-line explanation of what the configured scope does to the deductions on a check. */
export function holding_scope_explainer(scope: OpenDeductionsScope | undefined, account_label: string): string {
  switch (scope) {
    case "uncoded_only":
      return `Fully coded deductions post straight to their GL accounts; anything whose coding isn't finished parks in ${account_label}, so coding is not required to apply. Finish the parked ones later from the Open Deductions screen.`
    case "by_stage":
      return `Deductions whose stage is Validated or Expensable and whose coding is complete post straight to their GL accounts; everything else on this check posts to ${account_label}. MarginWiz keeps the real coding — once a parked deduction's stage is closed out, sync it from the Open Deductions screen to move the dollars to its GL account.`
    default:
      return `Every deduction on this check posts to ${account_label} — coding is not required to apply. Clear them to their GL accounts later from the Open Deductions screen.`
  }
}

interface OpenDeductionsAccountControlProps {
  /** The accounting integration whose settings hold the account. */
  integration_id: string
  accounting_system: string
  account?: string
  account_name?: string
  scope?: OpenDeductionsScope
  ledger_accounts: LedgerAccountJson[]
  /** Called after a successful save so the preview can be rebuilt with the new setting. */
  onSaved: () => Promise<void> | void
}

/**
 * The open-deductions banner on the cash-application preview, with the account picker inline: the
 * brand picks (or changes) the GL account parked deductions post to without leaving the check, and
 * the preview rebuilds against the new setting. Saves through the integration settings endpoint, so
 * it is the same value the Settings page shows.
 */
export function OpenDeductionsAccountControl({
  integration_id,
  accounting_system,
  account,
  account_name,
  scope,
  ledger_accounts,
  onSaved,
}: OpenDeductionsAccountControlProps) {
  const [editing, setEditing] = useState(false)
  const [draft_account, setDraftAccount] = useState<string | null>(account ?? null)
  const [draft_scope, setDraftScope] = useState<OpenDeductionsScope>(scope ?? (accounting_system === "NetSuite" ? "by_stage" : "all_deductions"))
  const [saving, setSaving] = useState(false)

  const options = holding_account_options(ledger_accounts)
  const account_label = account ? `${account}${account_name ? ` (${account_name})` : ""}` : ""

  async function handleSave() {
    setSaving(true)
    // Empty string clears the setting — the settings merge ignores nulls.
    const res = await updateIntegration(integration_id, {
      data: { open_deductions_account_id: draft_account ?? "", open_deductions_scope: draft_scope },
    })
    setSaving(false)
    if (!res.ok) {
      toast({ title: "Save failed", description: res.error.message, variant: "destructive" })
      return
    }
    toast({
      title: "Open-deductions account saved",
      description: draft_account ? `Deductions now post to ${draft_account} when cash is applied.` : "Deductions must be coded before applying.",
    })
    setEditing(false)
    await onSaved()
  }

  const picker = (
    <div class="mt-3 flex flex-wrap items-end gap-3">
      <div class="space-y-1">
        <Label class="text-xs">Open-deductions account</Label>
        <AutocompleteSelect
          values={options}
          initial={options.find((o: SelectItem) => o.id === (draft_account ?? NONE_ID))}
          onSelect={(item: SelectItem) => setDraftAccount(item.id === NONE_ID ? null : item.id)}
        />
      </div>
      {draft_account && (
        <div class="space-y-1 min-w-[22rem]">
          <Label class="text-xs">What parks, and when it syncs</Label>
          <Select value={draft_scope} onValueChange={(v: string) => setDraftScope(v as OpenDeductionsScope)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OPEN_DEDUCTIONS_SCOPE_OPTIONS.map((option) => (
                <SelectOption key={option.value} value={option.value}>
                  {option.label}
                </SelectOption>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <Button size="sm" onClick={handleSave} disabled={saving}>
        {saving ? "Saving…" : "Save and rebuild preview"}
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={saving}>
        Cancel
      </Button>
    </div>
  )

  if (!account) {
    return (
      <div class="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
        <div class="flex items-center justify-between gap-3">
          <span>
            Deductions on this check post to their coded GL accounts in {accounting_system}. To park them in an open-deductions
            account instead (and expense them later by stage), pick the account here.
          </span>
          {!editing && (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              Set holding account…
            </Button>
          )}
        </div>
        {editing && picker}
      </div>
    )
  }

  return (
    <div class="rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
      <div class="flex items-start justify-between gap-3">
        <span>{holding_scope_explainer(scope, account_label)}</span>
        <div class="flex shrink-0 items-center gap-2">
          <Link href="/accounting/open-deductions" class="whitespace-nowrap text-sky-800 underline">
            Open Deductions
          </Link>
          {!editing && (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              Change…
            </Button>
          )}
        </div>
      </div>
      {editing && picker}
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("holding_account_options", () => {
    it("leads with an explicit off option and de-duplicates account numbers", () => {
      const options = holding_account_options([
        { account_number: "1290", expense_account: "Open Deductions" },
        { account_number: "4602", expense_account: "Slotting" },
        // NetSuite syncs one ledger row per item, so an account can repeat.
        { account_number: "4602", expense_account: "Slotting" },
        { account_number: "", expense_account: "Unnumbered" },
      ])
      expect(options.map((o) => o.id)).toEqual(["__none__", "1290", "4602"])
      expect(options[1].name).toBe("1290 — Open Deductions")
    })
  })

  describe("holding_scope_explainer", () => {
    it("names the account and describes the stage gate for by_stage", () => {
      const text = holding_scope_explainer("by_stage", "1290 (Open Deductions)")
      expect(text).toContain("1290 (Open Deductions)")
      expect(text).toContain("Validated or Expensable")
      expect(text).toContain("post straight to their GL accounts")
      expect(holding_scope_explainer("uncoded_only", "1290")).toContain("coding isn't finished")
      expect(holding_scope_explainer(undefined, "1290")).toContain("coding is not required")
    })
  })
}
